import { BranchGate } from "../../core/gates/branch-gate.js";
import { SpecValidator } from "../../core/sdd/validator.js";
import { HandoffEngine } from "../../core/handoff/handoff-engine.js";
import { runCollectEvidence } from "./collect-evidence.js";
import { QualityGuard } from "../../core/validation/quality-guard.js";
import { HealerEngine } from "../../core/healing/healer-engine.js";
import { createCliRemediationExecutor } from "../../core/healing/cli-remediation-executor.js";
import { isRecoverableGateFailure, isTerminalFailure } from "../../core/statuses.js";
import path from "node:path";
import fs from "node:fs/promises";
import { getWorkflowProfile, resolveWorkflowProfile } from "../../core/workflow-profiles.js";
import { ApprovalReceiptValidator } from "../../core/sdd/approval-receipt.js";
import { TechnicalPlanValidator } from "../../core/sdd/technical-plan-validator.js";
import { hashFile, sha256, writeFileAtomic } from "../../core/sdd/workflow-artifacts.js";
import { EvidenceLedger } from "../../core/evidence/evidence-ledger.js";
import { DelegationController } from "../../core/delegation-controller.js";
import { OpenCodeAdapter } from "../../core/runtime/opencode-adapter.js";
import { Finalizer } from "../../core/finalization/finalizer.js";
import { execFileSync } from "node:child_process";
import { EvidenceValidator } from "../../core/validation/evidence-validator.js";

function normalizeTaskSlugFromSpec(specPath: string): string {
  const normalized = String(specPath).replaceAll("\\", "/");
  const marker = "/docs/workflows/";
  const index = normalized.includes(marker)
    ? normalized.indexOf(marker) + marker.length
    : normalized.startsWith("docs/workflows/") ? "docs/workflows/".length : -1;
  if (index >= 0) {
    const slug = normalized.slice(index).split("/")[0];
    if (slug) return slug;
  }
  return path.basename(specPath, path.extname(specPath));
}

export function combineValidation(evidence: any, quality: any): any {
  const statuses = [evidence.internalStatus, quality.overallStatus];
  let overallStatus = "PASS";
  if (statuses.includes("BLOCKED")) overallStatus = "BLOCKED";
  else if (statuses.includes("FAIL")) overallStatus = "FAIL";
  else if (statuses.includes("FAIL_DELEGATION_GATE")) overallStatus = "FAIL_DELEGATION_GATE";
  else if (statuses.includes("FAIL_QUALITY_GATE")) overallStatus = "FAIL_QUALITY_GATE";
  else if (statuses.includes("PASS_WITH_NOTES")) overallStatus = "PASS_WITH_NOTES";
  return { overallStatus, evidence, quality };
}

export interface runMasterOrchestratorOptions {
  cwd: string;
  specPath?: string;
  approvalPath?: string;
  override?: string;
  remediationExecutor?: any;
}

interface DeepWorkflowContext {
  workflowId: string;
  baseSha: string;
  artifactHashes: {
    draft: string | null;
    approved: string;
    technicalPlan: string;
  };
  phaseOrder: string[];
  confirmedActors: Array<{ phase: string; requested: string; applied: string }>;
  ledger: EvidenceLedger;
  ledgerPath: string;
  controller: DelegationController;
}

function repositoryRelative(cwd: string, candidate: string): string {
  return path.relative(cwd, path.resolve(cwd, candidate)).replaceAll("\\", "/");
}

async function findCompatibleLedger(cwd: string, specRelativePath: string): Promise<string[]> {
  const historyDir = path.join(cwd, ".ai-workflow/history");
  const matches: string[] = [];
  for (const file of await fs.readdir(historyDir).catch(() => [])) {
    if (!file.endsWith("-ledger.json")) continue;
    try {
      const events = JSON.parse(await fs.readFile(path.join(historyDir, file), "utf8"));
      if (events.some((event: any) => event.eventType === "specification_complete" && event.data?.artifactPath === specRelativePath)) {
        matches.push(path.join(historyDir, file));
      }
    } catch {
      // Invalid ledgers are not silently attached; the canonical path is checked below.
    }
  }
  return matches;
}

async function runDeepImplementation(
  cwd: string,
  absoluteSpecPath: string,
  specPath: string,
  approvalPath: string | undefined,
  specContent: string
): Promise<DeepWorkflowContext> {
  if (!approvalPath) throw new Error("[SDD BLOCKED] DEEP resume requires --approval-path.");

  const approvalValidator = new ApprovalReceiptValidator({ cwd });
  const approval = await approvalValidator.validate(approvalPath, absoluteSpecPath);
  if (!approval.valid || !approval.receipt || !approval.approvedSpecHash) {
    throw new Error(`[SDD BLOCKED] ${approval.reason || "Approval receipt is invalid."}`);
  }

  const workflowId = approval.receipt.workflowId;
  const ledgerPath = `.ai-workflow/history/${workflowId}-ledger.json`;
  const absoluteLedgerPath = path.join(cwd, ledgerPath);
  const relativeSpecPath = repositoryRelative(cwd, specPath);
  const compatibleLedgers = await findCompatibleLedger(cwd, relativeSpecPath);
  if (compatibleLedgers.length > 1) {
    throw new Error("[SDD BLOCKED] Multiple ledgers claim the same approved specification.");
  }
  if (compatibleLedgers.length === 1 && path.resolve(compatibleLedgers[0]) !== path.resolve(absoluteLedgerPath)) {
    throw new Error("[SDD BLOCKED] Approval workflowId conflicts with the ledger that created the specification.");
  }

  const ledger = new EvidenceLedger({ cwd, workflowId });
  const loaded = await ledger.load(ledgerPath);
  if (!loaded) {
    ledger.logEvent({
      actor: "Atlas",
      actorType: "control-plane",
      observed: true,
      eventType: "workflow_attached",
      provenance: "sdd-control-plane",
      data: { specSource: "external", specPath: relativeSpecPath, status: "COMPLETED" }
    });
  }

  const events = ledger.getEvents();
  const approvalEvents = events.filter((event) => event.eventType === "spec_approval_verified");
  if (approvalEvents.length > 1) throw new Error("[SDD BLOCKED] Duplicate specification approval events in ledger.");
  if (approvalEvents.length === 1 && (
    approvalEvents[0].data?.approvedHash !== approval.approvedSpecHash ||
    approvalEvents[0].data?.approvalPath !== repositoryRelative(cwd, approvalPath)
  )) throw new Error("[SDD BLOCKED] Existing specification approval conflicts with the supplied receipt.");
  const nexusTerminals = events.filter((event) => event.eventType === "specification_complete");
  if (nexusTerminals.length > 1) throw new Error("[SDD BLOCKED] Duplicate Nexus terminal events in ledger.");
  if (nexusTerminals.length === 1 && nexusTerminals[0].data?.artifactPath !== relativeSpecPath) {
    throw new Error("[SDD BLOCKED] Ledger specification path does not match --spec-path.");
  }

  const draftHash = nexusTerminals[0]?.data?.artifactHash || null;
  const controller = new DelegationController({ cwd, ledger, adapter: new OpenCodeAdapter({ cwd }) });
  const absolutePlanPath = path.join(cwd, "docs/workflows", workflowId, "technical-plan.json");
  const relativePlanPath = repositoryRelative(cwd, absolutePlanPath);
  if (await fs.access(absolutePlanPath).then(() => true).catch(() => false)) {
    throw new Error(`[SDD BLOCKED] Refusing to overwrite existing technical plan: ${relativePlanPath}`);
  }

  const planValidator = new TechnicalPlanValidator({ cwd });
  const protectedPaths = [absoluteSpecPath, approvalPath, ledgerPath, absolutePlanPath, "EVIDENCE.json"];
  const planning = await controller.executePhase({
    phase: "planning",
    actor: "Orion",
    prompt: [
      "Return only a JSON technical plan conforming to dist-assets/schemas/technical-plan.schema.json.",
      "Use explicit repository-relative file paths in filesInScope. Do not edit files or implement.",
      "Approved specification:",
      specContent
    ].join("\n\n"),
    readOnly: true,
    requireExplicitActor: true,
    onOutput: async (output) => {
      const validation = await planValidator.validateOutput(output, protectedPaths);
      if (!validation.valid || !validation.content) throw new Error(validation.reason || "Technical plan is invalid.");
      await writeFileAtomic(absolutePlanPath, validation.content);
      return { artifactPath: relativePlanPath, artifactHash: sha256(validation.content) };
    }
  });
  await ledger.save(ledgerPath);
  if (!planning.success || !planning.artifact?.artifactHash) {
    throw new Error(`[WORKFLOW BLOCKED] Orion planning phase failed: ${planning.error || "unknown failure"}`);
  }
  if (approvalEvents.length === 0) {
    ledger.logEvent({
      actor: "Atlas",
      actorType: "control-plane",
      observed: true,
      eventType: "spec_approval_verified",
      provenance: "approval-receipt",
      data: {
        approvalPath: repositoryRelative(cwd, approvalPath),
        approvedHash: approval.approvedSpecHash,
        draftHash,
        authorizationSource: approval.receipt.authorizationSource,
        status: "COMPLETED"
      }
    });
    await ledger.save(ledgerPath);
  }

  const planContent = await fs.readFile(absolutePlanPath, "utf8");
  const parsedPlan = JSON.parse(planContent);
  const approvedHash = approval.approvedSpecHash;
  const planHash = String(planning.artifact.artifactHash);
  const baseSha = (() => {
    try {
      return execFileSync("git", ["rev-parse", "HEAD"], { cwd, encoding: "utf8", stdio: "pipe" }).trim();
    } catch {
      return "unknown";
    }
  })();
  const finalizer = new Finalizer({ cwd, fastTrack: false });
  const beforeImplementation = await finalizer.snapshotManager.capture();
  const allowedFiles = new Set(parsedPlan.filesInScope.map((entry: any) => String(entry.path).replaceAll("\\", "/")));

  const implementation = await controller.executePhase({
    phase: "implementation",
    actor: "Astra",
    prompt: [
      "Implement the approved specification using the validated technical plan.",
      `You may modify only these files: ${[...allowedFiles].join(", ")}`,
      "Do not modify the specification, approval receipt, technical plan, ledger, or evidence artifacts.",
      "Approved specification:",
      specContent,
      "Validated technical plan:",
      planContent
    ].join("\n\n"),
    readOnly: false,
    requireExplicitActor: true,
    onOutput: async () => {
      if (await hashFile(absoluteSpecPath) !== approvedHash) throw new Error("Approved specification changed during Astra implementation.");
      if (await hashFile(absolutePlanPath) !== planHash) throw new Error("Technical plan changed during Astra implementation.");
      const integrity = await finalizer.verifyIntegrity(beforeImplementation);
      const workspaceStateMarkers = new Set(["stagedDiff", "unstagedDiff", "untrackedFiles"]);
      const changed = [...integrity.changes.added, ...integrity.changes.modified, ...integrity.changes.deleted]
        .filter((entry) => !workspaceStateMarkers.has(entry) && !/^(branch|head|indexTree):/.test(entry))
        .map((entry) => String(entry).replaceAll("\\", "/"));
      const outsideScope = changed.filter((entry) => !allowedFiles.has(entry));
      if (outsideScope.length > 0) throw new Error(`Astra modified files outside the technical plan: ${outsideScope.join(", ")}`);
      return { artifactPath: relativePlanPath, artifactHash: planHash };
    }
  });
  await ledger.save(ledgerPath);
  if (!implementation.success) {
    throw new Error(`[WORKFLOW BLOCKED] Astra implementation phase failed: ${implementation.error || "unknown failure"}`);
  }

  const phaseEvents = ledger.getEvents().filter((event) =>
    ["specification_complete", "planning_complete", "implementation_complete"].includes(event.eventType) && event.data?.success !== false
  );
  return {
    workflowId,
    baseSha,
    artifactHashes: { draft: draftHash, approved: approvedHash, technicalPlan: planHash },
    phaseOrder: phaseEvents.map((event) => event.data?.phase || event.eventType.replace("_complete", "")),
    confirmedActors: phaseEvents
      .filter((event) => event.data?.runtimeAgentApplied)
      .map((event) => ({
        phase: event.data.phase || event.eventType.replace("_complete", ""),
        requested: event.data.runtimeAgentRequested,
        applied: event.data.runtimeAgentApplied
      })),
    ledger,
    ledgerPath,
    controller
  };
}

/**
 * Coordinates branch safety, specification validation, observed validation, bounded remediation, and handoff.
 * Failed required validation can never be promoted to success.
 */
export async function runMasterOrchestrator({ cwd, specPath, approvalPath, override, remediationExecutor = null }: runMasterOrchestratorOptions): Promise<any> {
  if (process.env.AI_WORKFLOW_FINALIZATION_OWNER === "execute") {
    throw new Error("[NESTED EXECUTION BLOCKED] The parent execute process owns orchestration and finalization.");
  }

  console.log("\n[AI WORKFLOW] Starting evidence-backed orchestration...\n");

  const branchGate = new BranchGate({ memoryDir: path.join(cwd, ".ai-workflow"), cwd });
  const taskSlug = specPath ? normalizeTaskSlugFromSpec(specPath) : "implementation";
  const gateResult = branchGate.check(override, { taskSlug });
  if (gateResult.blocked) throw new Error(`[GATE BLOCKED] ${gateResult.reason}`);
  console.log(`[PASS] Branch Gate: ${gateResult.recovered ? `${gateResult.branchBefore} -> ${gateResult.branch}` : `${gateResult.branch} is authorized`}.`);

  if (!specPath) throw new Error("[SDD BLOCKED] Missing --spec-path. An approved specification is required.");

  const validator = new SpecValidator();
  const absoluteSpecPath = path.isAbsolute(specPath) ? specPath : path.join(cwd, specPath);
  const validation = await validator.validate(absoluteSpecPath);
  if (!validation.valid) throw new Error(`[SDD BLOCKED] Specification is not ready: ${validation.reason}`);

  const specTier = validation.tier || "standard";
  const riskLevel = specTier === "deep" ? "high" : specTier === "tiny" ? "low" : "medium";
  const evidencePolicy = specTier === "deep" ? "required" : "optional";
  const remediationLimit = specTier === "deep" ? 3 : specTier === "tiny" ? 1 : 2;

  const specContent = await fs.readFile(absoluteSpecPath, "utf8");
  const workflowProfile = resolveWorkflowProfile({ request: specContent });
  const profileDefinition = getWorkflowProfile(workflowProfile);
  console.log(`[PASS] Specification: ${specTier.toUpperCase()} spec is APPROVED.`);
  console.log(`[PROFILE] ${workflowProfile} -> ${profileDefinition.owner}; skills: ${profileDefinition.skills.join(", ") || "none"}.`);

  const deepContext = specTier === "deep"
    ? await runDeepImplementation(cwd, absoluteSpecPath, specPath, approvalPath, specContent)
    : null;

  const validateWorkflow = async () => {
    const evidence = await runCollectEvidence({
      cwd,
      exitOnError: false,
      taskSlug,
      evidencePolicy,
      profile: workflowProfile,
      branchRecovery: gateResult.recovered ? `${gateResult.branchBefore} -> ${gateResult.branch}` : "NOT_REQUIRED",
      workflowEvidence: deepContext ? {
        workflowId: deepContext.workflowId,
        baseSha: deepContext.baseSha,
        artifactHashes: deepContext.artifactHashes,
        phaseOrder: deepContext.phaseOrder,
        confirmedActors: deepContext.confirmedActors
      } : null
    });
    const quality = await new QualityGuard({ cwd, taskSlug, evidencePolicy, riskLevel }).verify();
    const combined = combineValidation(evidence, quality);
    if (deepContext) {
      const workflowEvidence = await new EvidenceValidator({ cwd, requireWorkflowEvidence: true }).validate();
      if (!workflowEvidence.valid) {
        combined.overallStatus = "FAIL_QUALITY_GATE";
        combined.reason = workflowEvidence.reason;
      }
    }
    return combined;
  };

  const observedValidate = async () => deepContext
    ? await deepContext.controller.validate(taskSlug, workflowProfile, validateWorkflow, evidencePolicy)
    : await validateWorkflow();

  let result = await observedValidate();
  if (deepContext) await deepContext.ledger.save(deepContext.ledgerPath);
  if (isRecoverableGateFailure(result.overallStatus)) {
    console.log(`\n[REMEDIATION REQUIRED] ${result.overallStatus}. Starting bounded remediation (limit: ${remediationLimit}).`);
    // Use the provided executor (programmatic API) or fall back to the default CLI executor.
    // The CLI executor re-runs failed npm scripts; it does not modify source code.
    const executor = typeof remediationExecutor === "function"
      ? remediationExecutor
      : createCliRemediationExecutor(cwd);
    const healer = new HealerEngine({ cwd, remediationLimit, taskSlug });
    result = await healer.run({ initialResult: result, validate: observedValidate, remediate: executor });
    if (deepContext) await deepContext.ledger.save(deepContext.ledgerPath);
  }

  if (isTerminalFailure(result.overallStatus)) {
    throw new Error(`[WORKFLOW BLOCKED] ${result.overallStatus}: ${result.reason || "Validation could not be resolved safely."}`);
  }
  if (isRecoverableGateFailure(result.overallStatus)) {
    throw new Error(`[WORKFLOW BLOCKED] Unresolved gate status after remediation: ${result.overallStatus}`);
  }

  console.log("\n--- Final Handoff ---");
  const handoffEngine = new HandoffEngine({ cwd });
  const handoffPath = await handoffEngine.generate({
    taskId: path.basename(specPath, ".md"),
    status: result.overallStatus,
    specPaths: [specPath],
    evidence: result.evidence,
    nextActions: `Profile ${workflowProfile}. Observed validation completed. Ready for review or explicitly approved release actions.`
  });

  console.log(`\n[AI WORKFLOW COMPLETE] ${result.overallStatus}`);
  if (result.remediation?.attempts) console.log(`Remediation attempts: ${result.remediation.attempts}`);
  console.log(`Handoff Packet: ${path.relative(cwd, handoffPath)}\n`);
  return { ...result, workflowProfile, workflowId: deepContext?.workflowId || null };
}
